import { viewingAsUser } from './impersonation-guard';

/**
 * Admin v2 "view as user" (security review M4): a tab that views as a user must not feed third-party analytics,
 * session recording or ads. The admin's screen is not the user's visit, and Clarity would record the user's private
 * pages (files, billing, email) under the admin's browser id.
 *
 * DI-free and tiny (it ships in `_app`): `_app` asks `analyticsBlockedByImpersonation()` before it loads Clarity,
 * `MotivesService` asks it before it loads AdSense, and `pages/_document.tsx` makes the same check inline before
 * `gtag('config')`. If a session starts after they loaded (the admin page in the moment before it navigates away, or a
 * copied token reported by the server), `stopThirdPartyAnalytics()` turns them off in place.
 */

/** The GA4 property configured in `pages/_document.tsx`. */
export const GA_MEASUREMENT_ID = 'G-SJCXH1QF9N';

/** A record in this tab, or the stored bearer itself is an impersonation token (copied token). */
export function analyticsBlockedByImpersonation(): boolean {
    return viewingAsUser();
}

let stopped = false;

/** Disables GA, Clarity and ad personalisation for the rest of this page. Idempotent; never throws. */
export function stopThirdPartyAnalytics(): void {
    if (stopped || typeof window === 'undefined') return;
    stopped = true;
    const w = window as any;
    try {
        // GA's documented opt-out: gtag.js checks this flag before it sends any hit.
        w[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
        if (typeof w.gtag === 'function') {
            w.gtag('consent', 'update', {
                analytics_storage: 'denied',
                ad_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied',
            });
        }
    } catch { /* analytics never breaks the page */ }
    try {
        if (typeof w.clarity === 'function') {
            w.clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' });
            w.clarity('stop');
        }
    } catch { /* analytics never breaks the page */ }
}

/** Test seam. */
export function resetThirdPartyAnalyticsStopForTests(): void {
    stopped = false;
}
